<script setup lang="ts">
import {
  PhArrowRight as ArrowRightIcon,
  PhClock as ClockIcon,
  PhPackage as PackageIcon,
} from '@phosphor-icons/vue'
import { onMounted, ref } from 'vue'
import ProductImage from '../components/ProductImage.vue'
import StatusState from '../components/StatusState.vue'
import { api } from '../services/api'
import type { CustomerOrder } from '../types'

const orders = ref<CustomerOrder[]>([])
const loading = ref(true)
const error = ref('')

function needsCustomerAction(order: CustomerOrder) {
  return order.confirmation.status === 'waiting'
    || order.payments.some((payment) => payment.status === 'waiting')
    || (order.inquiryStatus === 'accepted' && !order.survey.hasBooking)
}

async function loadOrders() {
  loading.value = true
  error.value = ''
  try {
    orders.value = await api.getMyOrders()
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : '订单加载失败'
  } finally {
    loading.value = false
  }
}

onMounted(loadOrders)
</script>

<template>
  <section class="page-hero page-hero--orders">
    <div class="container page-hero__inner">
      <div>
        <h1>我的定制进度</h1>
        <p>每个节点都显示当前状态、下一项任务和最近更新时间。</p>
      </div>
      <PackageIcon :size="78" weight="duotone" aria-hidden="true" />
    </div>
  </section>

  <section class="section">
    <div class="container">
      <StatusState v-if="loading" kind="loading" />
      <StatusState
        v-else-if="error"
        kind="error"
        title="订单暂时无法显示"
        :message="error"
        @retry="loadOrders"
      />
      <StatusState
        v-else-if="orders.length === 0"
        kind="empty"
        title="还没有定制订单"
        message="先从成熟柜款中选择颜色、尺寸和五金，提交第一份询价。"
      >
        <RouterLink class="button button--primary" to="/products">选择柜款</RouterLink>
      </StatusState>
      <div v-else class="order-list">
        <article
          v-for="order in orders"
          :key="order.id"
          class="order-card"
          :class="{ 'order-card--action-needed': needsCustomerAction(order) }"
        >
          <div class="order-card__image">
            <ProductImage :src="order.productImage" :alt="`${order.productName}展示图`" />
          </div>
          <div class="order-card__body">
            <div class="order-card__heading">
              <div>
                <span>{{ order.orderNo }}</span>
                <h2>{{ order.productName }}</h2>
              </div>
              <div class="order-card__badges">
                <span v-if="needsCustomerAction(order)" class="action-needed-badge">需要您处理</span>
                <span class="status-badge">{{ order.stageLabel }}</span>
              </div>
            </div>
            <dl class="order-card__meta">
              <div><dt>服务区域</dt><dd>{{ order.city }}</dd></div>
              <div><dt>最近更新</dt><dd>{{ order.updatedAt }}</dd></div>
            </dl>
            <div class="order-card__next">
              <ClockIcon :size="20" weight="duotone" aria-hidden="true" />
              <div>
                <span>下一步</span>
                <p>{{ order.nextAction }}</p>
              </div>
            </div>
            <RouterLink class="text-link" :to="`/orders/${order.id}`">
              查看完整进度
              <ArrowRightIcon :size="17" aria-hidden="true" />
            </RouterLink>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>
